// Data setup — the SQL export surface. Two halves over one `exports` collection:
// the "Form meta & SQL" tab's per-form exports (`form_vertical_export`), and the
// company-wide ones the SQL settings drawer manages. Plus one read that touches
// no export at all: the "Form structure" tab's field list.
//
// Every operation keys on something different, which is why each is its own
// mutation rather than one parameterised call:
//   · create / delete → the export RECORD
//   · company re-import → a TYPE     (POST exports/reimport-company-export)
//   · per-form re-import → a FORM    (POST exports/reimport-form-vertical-export)
//
// POST /dashboard-setup/list-forms takes `{ page, limit, search? }` — still no
// orderBy. That absence is load-bearing: the page ships without a single
// sortable column, rather than drawing affordances the endpoint can't honour.
// Widen DataSetupFormQueryArgs when the payload grows, not before.
//
// Connecting a form is the ONLY way its table name is persisted: there is no
// endpoint that saves a name on its own, so the DB-name cell holds a draft and
// addVerticalExport commits it. Disconnecting deletes the export ENTRY by its
// own id. A duplicate table name is rejected by the save itself (no pre-check
// call), and the response's `msg` is the useful part — callers surface it.
//
// `formStatus` rides the wire but no column renders it, so it stays on the raw
// shape and never reaches the domain row. `FormLifecycleStatus` and
// `FormPerson` are imported from formsApi rather than redeclared — the row's
// lifecycle enum and `createdBy` shape are byte-identical to the forms list,
// and one vocabulary beats two that can drift.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';
import type { FormLifecycleStatus, FormPerson, FormStatus } from './formsApi';

/** The backend's SqlTableExportType, verbatim. */
export enum SqlExportType {
  FormVertical = 'form_vertical_export',
  FormHorizontal = 'form_horizontal_export',
  FormStructure = 'form_structure_export',
  CompanyData = 'company_data_export',
  Attendance = 'attendance_export',
  Tasks = 'tasks_export',
  Users = 'users_export',
  PowerBi = 'powerbi_export',
}

/** What the company-export endpoints accept — add, delete and re-import all
 *  take these five. This is the CONTRACT set, deliberately kept apart from the
 *  list the SQL settings drawer renders (COMPANY_EXPORT_SECTIONS, four of
 *  these): form_structure_export is managed by the same endpoints but belongs
 *  to the Form structure tab, so it must not leak in as a fifth section there.
 *  It has its own one-element list, FORM_STRUCTURE_SECTION, rendered by the
 *  same drawer component from that tab. */
export const COMPANY_MANAGED_EXPORT_TYPES = [
  SqlExportType.Users,
  SqlExportType.Tasks,
  SqlExportType.Attendance,
  SqlExportType.CompanyData,
  SqlExportType.FormStructure,
] as const;

export type CompanyManagedExportType = (typeof COMPANY_MANAGED_EXPORT_TYPES)[number];

// ---- raw shapes (per the confirmed response) ----

interface ApiDataSetupPerson {
  _id: string;
  user: { _id: string; firstName: string; lastName: string };
}

/** One SQL export config — the same object whether it arrives embedded in a
 *  `list-forms` row or as an item of `exports/list`. On a form row `exports`
 *  holds either ONE of these or none, so there `type` is documentation rather
 *  than a selector; the drawer DOES select on it. */
interface ApiExportFormRef {
  formId: string;
  verticalTableName: string;
}

interface ApiExport {
  _id: string;
  tableName: string;
  type: SqlExportType;
  enableExport: boolean;
  /** When the export last ran. Absent until the first import. */
  updatedAt?: string | null;
  /** Forms attached to this export. Only powerbi_export populates it — every
   *  other type returns []. */
  forms?: ApiExportFormRef[];
}

/** GET exports/list — `{ items }`-wrapped, with no total. */
interface ApiExportsPage {
  items: ApiExport[];
}

/** GET exports/enabled-vertical-export-forms — `{ forms }`-wrapped. */
interface ApiEnabledVerticalFormsResponse {
  forms: { formId: string; verticalTableName: string; formTitle: string }[];
}

/** What a metadata entry can be. ONE array carries all three, so every reader
 *  filters: form-level option-set picks are this tab's, question-level ones
 *  belong to the Form structure tab, and topics to neither. */
export enum FormMetadataType {
  Topic = 'topic',
  QuestionSet = 'questionSet',
  FormSet = 'formSet',
}

/** One assignment on a form row. Every id arrives as a BARE string — nothing is
 *  populated, so there is no title and no option value anywhere on the row and
 *  the set catalogue is the only source of both. Which ids are filled depends on
 *  `type`, hence all of them nullable. `_id` is the assignment's own; no write
 *  addresses it, so it is never mapped. */
interface ApiFormMetadataEntry {
  _id: string;
  /** null for a form-level pick; the field's element id for a question-level
   *  one, which is how the Form structure tab will match its rows. */
  elementGlobalId: string | null;
  formSetId: string | null;
  formSetOptionId: string | null;
  topicId: string | null;
  type: FormMetadataType;
}

interface ApiDataSetupForm {
  _id: string;
  title: string;
  numberOfEntries: number;
  /** On the wire, but no column renders it — deliberately not mapped. */
  formStatus: FormLifecycleStatus;
  /** Working version id — always present. */
  currentVersion: string;
  /** Live published version id, or null when the form was never published. */
  publishedVersion: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: ApiDataSetupPerson | null;
  /** Set and topic assignments, all three kinds in one array. */
  metadata: ApiFormMetadataEntry[];
  exports: ApiExport[];
}

interface ApiDataSetupFormPage {
  items: ApiDataSetupForm[];
  totalRecords: number;
  /** The selected form sets — present ONLY when the request asked for them, so
   *  absent on every page past the first. Absent and `[]` are different facts:
   *  `[]` means nothing is selected. Never coalesced, here or downstream. */
  headerFormSetIds?: string[];
}

/** One row of POST /dashboard-setup/fetch-structure — one FIELD of one form.
 *  CONFIRMED against a live response, so required means required.
 *
 *  `form_area` also rides the wire and is deliberately not modelled. */
interface ApiFormStructureFields {
  form_id: string;
  form_title: string;
  /** The only nullable title: a media element (`field_type: 'image'`) has none. */
  field_title: string | null;
  field_id: string;
  /** Always sent, and unique per row — the row identity. */
  element_global_id: string;
  required: boolean;
  hidden: boolean;
  hint_text: string | null;
  scoring_field: boolean;
  /** Free text, and inconsistently cased at source — `TEXT FIELD`,
   *  `companystructure`, `Slider` all arrive as-is. Rendered verbatim rather
   *  than normalised: there is no canonical vocabulary to map against (forms'
   *  own `elementType` is a bare string too), so a mapping would be invented
   *  and a new type would silently render as the wrong thing. */
  field_type: string;
  /** The section NUMBER (`"0"`), always present; the title is usually null. */
  section: string;
  section_title: string | null;
  /** The form's page — NOT pagination. Renamed in the domain for that reason. */
  page: string;
  page_title: string;
  /** Another row's `field_id` when this field is revealed by conditional
   *  logic. The referenced row may be on another page of results. */
  triggered_by_field_id: string | null;
  folder: string | null;
  /** `"OPEN"` for an unscored/free field, else a numeric string (`"2"`). A
   *  sentinel, so it stays a string. NOTE the wire misspells "original" — kept
   *  verbatim here because raw types describe what arrives; the mapper renames
   *  it to `maxScore`. */
  max_origional_score: string;
  /** The topic's TITLE, not its id — so resolving it back to an id (which is
   *  what the save sends) is a catalogue lookup. See topicIdByTitle. */
  topics: string | null;
}

/** A structure row: the named fields above, PLUS one extra key per materialised
 *  question set, named after that set's `sqlColumn` and holding the picked
 *  option's VALUE (again a title, not an id). Which keys exist is a property of
 *  the response, not of this type — hence the open tail.
 *
 *  An intersection rather than an index signature on the interface itself: the
 *  named fields stay closed, documented and typo-checked, and only the unknown
 *  remainder is loose. */
type ApiFormStructureRow = ApiFormStructureFields & Record<string, unknown>;

/** POST /dashboard-setup/fetch-structure — `{ records, totalCount }`. A THIRD
 *  envelope in this one API: list-forms answers `{ items, totalRecords }` and
 *  exports/list `{ items }`. Same backend module, genuinely different shapes. */
interface ApiFormStructurePage {
  records: ApiFormStructureRow[];
  totalCount: number;
}

// ---- domain ----

/** Version-derived publish state. Narrowed from formsApi's FormStatus rather
 *  than redeclared — same two words, one vocabulary — but this list has no
 *  suspended arm: the row's lifecycle travels separately in `formStatus`, which
 *  no column renders. */
export type DataSetupFormState = Extract<FormStatus, 'published' | 'draft'>;

export interface DataSetupForm {
  id: string;
  title: string;
  entries: number;
  /** Published when the working version IS the live one; anything else reads as
   *  a draft — including never-published forms, where publishedVersion is null
   *  and so can never equal a currentVersion id. */
  state: DataSetupFormState;
  createdAt: string;
  updatedAt: string;
  createdBy: FormPerson | null;
  /** The `form_vertical_export` entry, flattened. No entry = never configured:
   *  no table name, export off. */
  dbName: string | null;
  dbConnection: boolean;
  /** That entry's own id — what a write will address. Null when there is none
   *  yet, which is also what tells a future write to create rather than patch. */
  exportId: string | null;
  /** This form's option-set picks, flattened to set id → chosen option id. Only
   *  the form-level ones; a flat map because every consumer asks "what did this
   *  form pick for this set", never "list the assignments". */
  setValues: Record<string, string>;
}

export interface DataSetupFormPage {
  items: DataSetupForm[];
  total: number;
  /** Passed through untouched — see the raw shape. A caller must distinguish
   *  `[]` ("nothing selected") from undefined ("this page wasn't asked"), so
   *  the guard is `if (headerFormSetIds)`, never `if (headerFormSetIds?.length)`. */
  headerFormSetIds?: string[];
}

/** The whole payload. No orderBy — the list cannot be sorted server-side. */
export interface DataSetupFormQueryArgs {
  page: number;
  limit: number;
  /** Omitted, never sent empty, when the search box is blank. */
  search?: string;
  /** Ask for `headerFormSetIds` — the selected sets, stated rather than derived
   *  from the rows. Only worth asking on page 1, so callers send `page === 1`.
   *  Required rather than optional: the one call site must not be able to
   *  forget it and silently lose the header list. */
  requireHeaders: boolean;
}

/** One form field, as the "Form structure" table renders it. */
export interface FormStructureField {
  /** Row identity — `element_global_id`, always sent and unique per row. */
  id: string;
  formId: string;
  formTitle: string;
  fieldTitle: string | null;
  fieldId: string;
  required: boolean;
  hidden: boolean;
  hintText: string | null;
  scoring: boolean;
  fieldType: string;
  /** Section NUMBER and title as separate values — the table shows a column
   *  each, so neither is folded into the other. */
  section: string;
  sectionTitle: string | null;
  /** The form's page — deliberately NOT `page`, which means pagination
   *  everywhere else in this file. */
  pageNumber: string;
  pageTitle: string;
  folder: string | null;
  /** The `fieldId` of the field whose answer reveals this one, or null. Kept as
   *  the raw id: the referenced field may sit on another page of results, so it
   *  cannot be resolved to a title from the loaded rows alone. */
  triggeredByFieldId: string | null;
  /** `"OPEN"` or a numeric string. Renamed off the wire's `max_origional_score`
   *  — the misspelling stops at the mapper. */
  maxScore: string;
  /** The topic TITLE, exactly as the row carries it. */
  topic: string | null;
  /** Question-set picks by LOWERCASED sqlColumn — the row's extra keys,
   *  collected here so the columns never touch the raw shape. Values are option
   *  TITLES, like `topic`. */
  setValues: Record<string, string>;
}

export interface FormStructurePage {
  items: FormStructureField[];
  total: number;
  /** Non-fixed column keys present on this page's records (LOWERCASED),
   *  value-independent — form-set AND question-set columns alike; the tab
   *  intersects them with the question-set catalogue. Column PRESENCE is how
   *  the tab knows which sets are selected: a selected set with no picks yet
   *  is an all-null column, which `setValues` (truthy strings only) rightly
   *  drops. */
  setColumns: string[];
}

/** The whole payload. No search, no orderBy, no filters — which is exactly why
 *  the tab draws none of those three controls. */
export interface FormStructureQueryArgs {
  page: number;
  limit: number;
}

/** The wire entry POST /dashboard-setup/update-metadata takes — the union of
 *  everything either tab can send, which is exactly why it is NOT exported:
 *  each tab's mutation maps its own narrower pick type onto this, filling the
 *  other side's nulls at the API boundary. */
interface FormMetadataAssignment {
  formId: string;
  elementGlobalId: string | null;
  formSetId: string | null;
  formSetOptionId: string | null;
  topicSetId: string | null;
}

/** One form-level option-set pick — the Form meta & SQL tab's save. A string
 *  option id assigns; NULL clears the pick the server currently holds. A cell
 *  that was never saved and sits on the placeholder sends nothing at all. */
export interface FormSetPick {
  formId: string;
  formSetId: string;
  formSetOptionId: string | null;
}

/** The Manage-sets save. `metadata` holds only the picks that CHANGED, and the
 *  two id lists are a delta rather than the full selection — so the caller keeps
 *  a baseline to measure against. That baseline is `headerFormSetIds` off the
 *  list — the selected sets stated by the server, not inferred from the rows —
 *  so the delta is correct from the first save of a session. */
export interface UpdateFormMetadataPayload {
  metadata: FormSetPick[];
  addedFormSetIds: string[];
  removedFormSetIds: string[];
}

/** One field-level pick — the Form structure tab's save. Discriminated on
 *  FormMetadataType, so "either a topic or an option set, never both" is
 *  structural rather than a comment: a member carrying both cannot be built.
 *  `kind` is a client discriminant only — the mutation strips it before the
 *  wire. A null id CLEARS. */
export type FieldMetadataPick =
  | {
      kind: FormMetadataType.Topic;
      formId: string;
      elementGlobalId: string;
      topicSetId: string | null;
    }
  | {
      kind: FormMetadataType.QuestionSet;
      formId: string;
      elementGlobalId: string;
      formSetId: string;
      formSetOptionId: string | null;
    };

/** Same route and delta shape as the form-level save; only the entries differ. */
export interface UpdateFieldMetadataPayload {
  metadata: FieldMetadataPick[];
  addedFormSetIds: string[];
  removedFormSetIds: string[];
}

/** A form attached to an export. The powerbi_export's `forms` IS the marked
 *  set, and the save payload wants both fields, so neither is dropped. */
export interface ExportFormRef {
  formId: string;
  verticalTableName: string;
}

/** One export record as the UI consumes it. `type` is the full SqlExportType,
 *  NOT narrowed to the managed five: exports/list also returns the
 *  powerbi_export the Power BI modal reads, so narrowing here would mean
 *  asserting something false about that item. The drawer looks up only its own
 *  four by key, so it is unaffected. */
export interface CompanyExport {
  id: string;
  type: SqlExportType;
  tableName: string;
  /** Null until the export has run at least once ("Never updated"). */
  updatedAt: string | null;
  /** Only powerbi_export populates this. */
  forms: ExportFormRef[];
}

/** One row of the Power BI modal's candidate list. */
export interface EnabledVerticalForm {
  formId: string;
  formTitle: string;
  verticalTableName: string;
}

// ---- mappers ----

const toPerson = (p: ApiDataSetupPerson | null): FormPerson | null => {
  if (!p) return null;
  const fullName = `${p.user.firstName} ${p.user.lastName}`.trim();
  return fullName ? { id: p._id, fullName } : null;
};

const toSetValues = (entries: ApiFormMetadataEntry[]): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const m of entries) {
    // Form-level option-set picks only: questionSet entries belong to the Form
    // structure tab, topic entries to neither, and all three ride this array.
    if (m.type !== FormMetadataType.FormSet) continue;
    if (!m.formSetId || !m.formSetOptionId) continue;
    out[m.formSetId] = m.formSetOptionId;
  }
  return out;
};

const toDataSetupForm = (f: ApiDataSetupForm): DataSetupForm => {
  const vertical = f.exports[0] ?? null;
  return {
    id: f._id,
    title: f.title,
    entries: f.numberOfEntries,
    state: f.currentVersion === f.publishedVersion ? 'published' : 'draft',
    createdAt: f.createdAt,
    updatedAt: f.updatedAt,
    createdBy: toPerson(f.createdBy),
    dbName: vertical?.tableName || null,
    dbConnection: vertical?.enableExport ?? false,
    exportId: vertical?._id ?? null,
    setValues: toSetValues(f.metadata),
  };
};

/** The named fields, so everything else on a row is a question-set column. Kept
 *  beside the interface it mirrors — a field added there must be added here or
 *  it would surface as a phantom column. */
const STRUCTURE_FIELD_KEYS = new Set([
  'form_id',
  'form_title',
  'field_title',
  'field_id',
  'element_global_id',
  'required',
  'hidden',
  'hint_text',
  'scoring_field',
  'field_type',
  'section',
  'section_title',
  'page',
  'page_title',
  'triggered_by_field_id',
  'folder',
  'max_origional_score',
  'topics',
  // On the wire, deliberately unmodelled — and it must not become a column.
  'form_area',
]);

/** Everything that isn't a named field: one entry per materialised question
 *  set, keyed by its LOWERCASED sqlColumn so the catalogue can match it
 *  case-insensitively. Non-string and empty values are dropped — an absent
 *  entry and a blank one mean the same thing (nothing picked), and keeping
 *  both would make the dirty check compare '' against undefined forever. */
const toStructureSetValues = (r: ApiFormStructureRow): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(r)) {
    if (STRUCTURE_FIELD_KEYS.has(k)) continue;
    if (typeof v === 'string' && v) out[k.toLowerCase()] = v;
  }
  return out;
};

const toFormStructureField = (r: ApiFormStructureRow): FormStructureField => ({
  // Always sent and unique per row, so no composite fallback and no index — a
  // page's rows key straight off the server's own element id.
  id: r.element_global_id,
  formId: r.form_id,
  formTitle: r.form_title,
  fieldTitle: r.field_title,
  fieldId: r.field_id,
  required: r.required,
  hidden: r.hidden,
  hintText: r.hint_text,
  scoring: r.scoring_field,
  fieldType: r.field_type,
  section: r.section,
  sectionTitle: r.section_title,
  pageNumber: r.page,
  pageTitle: r.page_title,
  folder: r.folder,
  triggeredByFieldId: r.triggered_by_field_id,
  maxScore: r.max_origional_score,
  topic: r.topics,
  setValues: toStructureSetValues(r),
});

const toCompanyExport = (e: ApiExport): CompanyExport => ({
  id: e._id,
  type: e.type,
  tableName: e.tableName,
  updatedAt: e.updatedAt ?? null,
  forms: e.forms ?? [],
});

export const dataSetupApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getDataSetupForms: build.query<DataSetupFormPage, DataSetupFormQueryArgs>({
      query: (body) => ({ ...ROUTES.dataSetup.listForms, data: body }),
      transformResponse: (res: ApiDataSetupFormPage) => ({
        items: res.items.map(toDataSetupForm),
        total: res.totalRecords,
        // Straight through, undefined and all — see the shape's comment.
        headerFormSetIds: res.headerFormSetIds,
      }),
      providesTags: [{ type: 'DataSetup', id: 'LIST' }],
    }),

    /** The "Form structure" tab: one row per field, across every template.
     *  Read-only — nothing in the app writes to it yet. The tag is
     *  forward-looking: its eventual invalidator is
     *  reimportCompanyExport(SqlExportType.FormStructure), once that export
     *  grows a UI. */
    getFormStructure: build.query<FormStructurePage, FormStructureQueryArgs>({
      query: (body) => ({ ...ROUTES.dataSetup.fetchStructure, data: body }),
      transformResponse: (res: ApiFormStructurePage) => {
        const records = res?.records ?? [];
        // Column PRESENCE, value-independent — see FormStructurePage.setColumns.
        const setColumns = new Set<string>();
        for (const r of records)
          for (const k of Object.keys(r))
            if (!STRUCTURE_FIELD_KEYS.has(k)) setColumns.add(k.toLowerCase());
        return {
          items: records.map(toFormStructureField),
          total: res?.totalCount ?? 0,
          setColumns: [...setColumns],
        };
      },
      providesTags: [{ type: 'DataSetup', id: 'STRUCTURE' }],
    }),

    /** Saves set assignments and which sets are enabled, in one call.
     *
     *  Invalidates the LIST because the write DOES change what list-forms
     *  returns — the row's `metadata[]` — so the refetch is what puts the saved
     *  picks back on screen and lets the page drop its drafts. */
    /** The Form meta & SQL tab's save: form-level option-set picks. The wire
     *  entry's other side is filled with nulls HERE, so page code never sees
     *  the five-field union. Invalidates LIST only — nothing a form-level pick
     *  writes is rendered by a structure row. */
    updateFormMetadata: build.mutation<unknown, UpdateFormMetadataPayload>({
      query: (body) => ({
        ...ROUTES.dataSetup.updateMetadata,
        data: {
          ...body,
          metadata: body.metadata.map(
            (m): FormMetadataAssignment => ({
              formId: m.formId,
              elementGlobalId: null,
              formSetId: m.formSetId,
              formSetOptionId: m.formSetOptionId,
              topicSetId: null,
            }),
          ),
        },
      }),
      invalidatesTags: [{ type: 'DataSetup', id: 'LIST' }],
    }),

    /** The Form structure tab's save: field-level topic and question-set picks.
     *  Same route; `kind` picks which side of the wire entry is real and is
     *  stripped before sending. Invalidates STRUCTURE only — nothing a
     *  field-level pick writes is rendered by a meta row (toSetValues keeps
     *  only form-level FormSet entries). */
    updateFieldMetadata: build.mutation<unknown, UpdateFieldMetadataPayload>({
      query: (body) => ({
        ...ROUTES.dataSetup.updateMetadata,
        data: {
          ...body,
          metadata: body.metadata.map(
            (m): FormMetadataAssignment =>
              m.kind === FormMetadataType.Topic
                ? {
                    formId: m.formId,
                    elementGlobalId: m.elementGlobalId,
                    formSetId: null,
                    formSetOptionId: null,
                    topicSetId: m.topicSetId,
                  }
                : {
                    formId: m.formId,
                    elementGlobalId: m.elementGlobalId,
                    formSetId: m.formSetId,
                    formSetOptionId: m.formSetOptionId,
                    topicSetId: null,
                  },
          ),
        },
      }),
      invalidatesTags: [{ type: 'DataSetup', id: 'STRUCTURE' }],
    }),

    /** Connect a form: creates its vertical export, which is also what persists
     *  `tableName`. Invalidates rather than patching optimistically — the query
     *  arg is the live `{ page, limit, search }`, so updateQueryData would have
     *  to reconstruct it exactly (the reason every paged module here uses
     *  tags). */
    addVerticalExport: build.mutation<unknown, { formId: string; tableName: string }>({
      query: (body) => ({ ...ROUTES.dataSetup.addVerticalExport, data: body }),
      invalidatesTags: [{ type: 'DataSetup', id: 'LIST' }],
    }),

    /** Deletes an export entry — a form's (DataSetupForm.exportId) or a
     *  company-wide one (CompanyExport.id); one route serves both. Invalidates
     *  BOTH ids because either surface can call it: one possibly redundant
     *  refetch beats two near-identical mutations. */
    removeExport: build.mutation<unknown, string>({
      query: (id) => ({ ...ROUTES.dataSetup.removeExport(id) }),
      invalidatesTags: [
        { type: 'DataSetup', id: 'LIST' },
        { type: 'DataSetup', id: 'COMPANY_EXPORTS' },
      ],
    }),

    /** Re-runs ONE form's vertical export. Keyed by the form, not the export —
     *  its own route, since the company one can't express a per-form target. */
    reimportFormVerticalExport: build.mutation<unknown, string>({
      query: (formId) => ({
        ...ROUTES.dataSetup.reimportFormVerticalExport,
        data: { formId },
      }),
      invalidatesTags: [{ type: 'DataSetup', id: 'LIST' }],
    }),

    /** The company-wide exports, for the SQL settings drawer. `{ items }`-
     *  wrapped; the caller filters to the types it renders, so this stays the
     *  whole list. */
    getCompanyExports: build.query<CompanyExport[], void>({
      query: () => ({ ...ROUTES.dataSetup.exportsList }),
      transformResponse: (res: ApiExportsPage) => (res?.items ?? []).map(toCompanyExport),
      providesTags: [{ type: 'DataSetup', id: 'COMPANY_EXPORTS' }],
    }),

    /** Create a company-wide export. The table name is derived in the frontend
     *  (companyExportSections), so it is passed in rather than typed. */
    addCompanyExport: build.mutation<
      unknown,
      { type: CompanyManagedExportType; tableName: string }
    >({
      query: (body) => ({ ...ROUTES.dataSetup.addCompanyExport, data: body }),
      invalidatesTags: [{ type: 'DataSetup', id: 'COMPANY_EXPORTS' }],
    }),

    /** Re-runs a company-wide export. Keyed by TYPE — it needs no export
     *  record. Accepts form_structure_export too, ready for that tab. */
    reimportCompanyExport: build.mutation<unknown, CompanyManagedExportType>({
      query: (type) => ({ ...ROUTES.dataSetup.reimportCompanyExport, data: { type } }),
      invalidatesTags: [{ type: 'DataSetup', id: 'COMPANY_EXPORTS' }],
    }),

    /** The Power BI modal's candidates: forms whose vertical export is live.
     *  Takes no params and returns the whole list, so the modal filters in
     *  memory — there is no pagination to misrepresent. */
    getEnabledVerticalForms: build.query<EnabledVerticalForm[], void>({
      query: () => ({ ...ROUTES.dataSetup.enabledVerticalExportForms }),
      transformResponse: (res: ApiEnabledVerticalFormsResponse) => res?.forms ?? [],
      // Rides the same tag: connecting or disconnecting a form changes which
      // forms are candidates here.
      providesTags: [{ type: 'DataSetup', id: 'COMPANY_EXPORTS' }],
    }),

    /** Marks which forms have a Power BI view. REPLACES the whole set — the
     *  body carries every marked form, not a delta. */
    addPowerBiExport: build.mutation<unknown, { tableName: string; forms: ExportFormRef[] }>({
      query: (body) => ({ ...ROUTES.dataSetup.addPowerBiExport, data: body }),
      invalidatesTags: [{ type: 'DataSetup', id: 'COMPANY_EXPORTS' }],
    }),
  }),
});

export const {
  useGetDataSetupFormsQuery,
  useGetFormStructureQuery,
  useUpdateFormMetadataMutation,
  useUpdateFieldMetadataMutation,
  useAddVerticalExportMutation,
  useRemoveExportMutation,
  useReimportFormVerticalExportMutation,
  useGetCompanyExportsQuery,
  useAddCompanyExportMutation,
  useReimportCompanyExportMutation,
  useGetEnabledVerticalFormsQuery,
  useAddPowerBiExportMutation,
} = dataSetupApi;
